import { FormValues, Graph, ImageType, OptionPair } from "@/types/productTypes";
import { produce } from "immer";

import fetchDb from "../utils/customRequestFunction";

import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";
import { AppEdge, AppNode } from "@/Modules/Admin/ReactFlow/OptionPairGraph";
import { buildVerticalLayout, LayoutData } from "@/utils/buildVertical";
import { type Node as RFNode, type Edge as RFEdge } from "@xyflow/react";
export interface GraphStoreState {
   traverseGraph: (
      graph: Graph,
      startNodeId: number,
      allValues: FormValues,
      visited?: Set<number>
   ) => ImageType[][];
   currentGraph: Graph | null;
   setCurrentGraph: (graph: Graph) => void;
   refreshGraphData: (
      setRfNodes: React.Dispatch<React.SetStateAction<RFNode[]>>,
      setRfEdges: React.Dispatch<React.SetStateAction<RFEdge[]>>
   ) => Promise<void>;
   handleConnect: (
      sourceNodeIds: number[],
      targetIds: number[],
      optionPairId: number
   ) => Promise<void>;
   handleBranch: (
      sourceNodeId: number,
      optionId: number,
      conditionItemIds: number[],
      optionPairId: number
   ) => Promise<void>;
   handleDelete: (nodeIds: string[]) => Promise<void>;
   handleAddOption: (
      sourceNodeIds: number[],
      optionId: number,
      optionPairId: number,
      each: boolean
   ) => Promise<void>;
   fetchOptionPair: (optionPairId: number) => Promise<OptionPair>;
   fetchGraphData: (
      optionPairId: number,
      setRfNodes: React.Dispatch<React.SetStateAction<RFNode[]>>,
      setRfEdges: React.Dispatch<React.SetStateAction<RFEdge[]>>,
      forceRefresh?: boolean
   ) => Promise<void>;
   graphCache: Record<
      number,
      {
         nodes: RFNode[];
         edges: RFEdge[];
         timestamp: number;
         mainImage: any;
      }
   >;
   clearGraphCache: (optionPairId?: number) => void;
   handleNodeAddition: (newNode: any, optionPairId: number) => Promise<any>;
   fetchLazyGraphData: (
      optionPairId: number,
      setRfNodes: React.Dispatch<React.SetStateAction<RFNode[]>>,
      setRfEdges: React.Dispatch<React.SetStateAction<RFEdge[]>>
   ) => Promise<void>;
}

export const createGraphSlice: AdminSliceCreator<GraphStoreState> = (
   set,
   get
) => ({
   currentGraph: null,

   setCurrentGraph: (graph) => {
      set(
         produce((state: AdminStoreState) => {
            state.currentGraph = graph;
         })
      );
   },

   graphCache: {} as Record<
      number,
      {
         nodes: RFNode[];
         edges: RFEdge[];
         timestamp: number;
         mainImage: any;
      }
   >,

   fetchGraphData: async (
      optionPairId,
      setRfNodes,
      setRfEdges,
      forceRefresh = false
   ) => {
      if (!optionPairId) return;

      const cache = get().graphCache[optionPairId];
      const now = Date.now();

      if (!forceRefresh && cache && now - cache.timestamp < 30000) {
         console.log("Using cached graph data");
         setRfNodes(cache.nodes);
         setRfEdges(cache.edges);
         return;
      }

      try {
         console.log("Fetching fresh graph data");
         const response = await fetchDb.get<OptionPair>(
            `/api/option-pair/${optionPairId}`
         );
         const currentOptionPair: OptionPair = response;

         const graph: Record<number, AppNode> = {};
         currentOptionPair.nodes.forEach((node) => {
            graph[node.id] = node;
         });

         const { layoutPositions }: LayoutData = buildVerticalLayout(graph);

         const flowNodes: RFNode[] = currentOptionPair.nodes.map(
            (appNode: AppNode) => ({
               id: appNode.id.toString(),
               type: "imagesNode",
               data: {
                  label: appNode.nodeLabel,
                  imagesInNode: appNode.imagesInNode,
                  items:
                     appNode.incomingEdges.length > 0
                        ? appNode.incomingEdges[0].conditionItems
                        : [],
                  nodeId: appNode.id,
                  previousNode:
                     appNode.incomingEdges.length > 0
                        ? appNode.incomingEdges[0].sourceNodeId
                        : undefined,
                  nextNode:
                     appNode.outgoingEdges.length > 0
                        ? appNode.outgoingEdges[0].targetNodeId
                        : undefined,
               },
               position: layoutPositions[appNode.id] || { x: 0, y: 0 },
            })
         );

         const flowEdges: RFEdge[] = currentOptionPair.edges.map(
            (appEdge: AppEdge) => ({
               id: `e${appEdge.sourceNodeId}-${appEdge.targetNodeId}`,
               source: appEdge.sourceNodeId.toString(),
               target: appEdge.targetNodeId.toString(),
               animated: true,
               label: appEdge.conditionItems
                  .map((item) => item.name)
                  .join(", "),
               type: "custom",
            })
         );

         set(
            produce((state: AdminStoreState) => {
               state.graphCache[optionPairId] = {
                  nodes: flowNodes,
                  edges: flowEdges,
                  timestamp: now,
                  mainImage: state.mainImage,
               };
            })
         );

         setRfNodes(flowNodes);
         setRfEdges(flowEdges);
      } catch (error) {
         console.error("Error fetching Option Pair:", error);
      }
   },

   refreshGraphData: async (setRfNodes, setRfEdges) => {
      const optionPairId = get().currentOptionPair?.id;
      if (!optionPairId) return;

      try {
         await get().fetchGraphData(optionPairId, setRfNodes, setRfEdges, true);
      } catch (error) {
         console.error("Error refreshing graph data:", error);
      }
   },

   traverseGraph: (
      graph,
      startNodeId,
      allValues,
      visited = new Set<number>()
   ) => {
      if (!graph[startNodeId] || visited.has(startNodeId)) return [];
      visited.add(startNodeId);
      const currentNode = graph[startNodeId];
      const collectedImages: ImageType[][] = [];

      const itemIdsWeCareAbout = new Set(
         Object.values(allValues).map((val) =>
            "selectedItem" in val ? val.selectedItem : null
         )
      );

      for (const imgNode of currentNode.imagesInNode) {
         if (
            imgNode.itemId?.toString() &&
            itemIdsWeCareAbout.has(imgNode.itemId.toString())
         ) {
            collectedImages.push([imgNode.image]);
         }
      }

      if (currentNode.outgoingEdges && currentNode.outgoingEdges.length > 0) {
         for (const edge of currentNode.outgoingEdges) {
            if (edge.conditionItems.length > 0) {
               const firstCondItem = edge.conditionItems[0];
               if (
                  firstCondItem.optionId &&
                  allValues[firstCondItem.optionId]
               ) {
                  const itemIdToMatch = allValues[firstCondItem.optionId];
                  if (!("selectedItem" in itemIdToMatch)) {
                     throw new Error("Missing selectedItem in form values");
                  }
                  const foundMatch = edge.conditionItems.some(
                     (ci) =>
                        ci.id && ci.id.toString() === itemIdToMatch.selectedItem
                  );
                  if (foundMatch && itemIdToMatch.selected) {
                     const imagesFromTarget = get().traverseGraph(
                        graph,
                        edge.targetNodeId,
                        allValues,
                        visited
                     );
                     collectedImages.push(...imagesFromTarget);
                  }
               }
            }
         }
      }
      return collectedImages;
   },

   fetchOptionPair: async (optionPairId: number) => {
      try {
         const response = await fetchDb.get<OptionPair>(
            `/api/option-pair/${optionPairId}`
         );
         return response;
      } catch (error) {
         console.error("Error fetching Option Pair:", error);
         throw error;
      }
   },

   handleConnect: async (
      sourceNodeIds: number[],
      targetIds: number[],
      optionPairId: number
   ) => {
      const dataSending = {
         optionPairId,
         sourceNodeIds,
         targetIds,
      };

      try {
         await fetchDb.post("/api/graph/connect", dataSending);
         await get().refreshGraphData();
      } catch (error) {
         console.error("Error connecting nodes:", error);
         throw error;
      }
   },

   handleBranch: async (
      sourceNodeId: number,
      optionId: number,
      conditionItemIds: number[],
      optionPairId: number
   ) => {
      try {
         await fetchDb.post("/api/graph/branch", {
            optionPairId,
            sourceNodeId,
            optionId,
            conditionItemIds,
         });
         return { success: true };
      } catch (error) {
         console.error("Error branching nodes:", error);
         throw error;
      }
   },

   handleDelete: async (nodeIds: string[]) => {
      try {
         const optionPairId = get().currentOptionPair?.id;
         if (!optionPairId) {
            console.error("No option pair selected");
            return;
         }

         await fetchDb.delete(`/api/graph/delete-nodes`, {
            data: {
               ids: nodeIds,
               optionPairId,
            },
         });

         return { success: true };
      } catch (error) {
         console.error("Error deleting nodes:", error);
         throw error;
      }
   },

   handleAddOption: async (
      sourceNodeIds: number[],
      optionId: number,
      optionPairId: number,
      each: boolean
   ) => {
      const dataSending = {
         optionPairId,
         sourceNodeIds,
         optionId,
      };

      try {
         if (each) {
            await fetchDb.post("/api/graph/node/each", dataSending);
            return { success: true };
         } else {
            return await get().handleConnect(
               sourceNodeIds,
               [optionPairId],
               optionPairId
            );
         }
      } catch (error) {
         console.error("Error adding option:", error);
         throw error;
      }
   },
   handleAddAndConnectionOptions: async () => {},

   clearGraphCache: (optionPairId?: number) => {
      set(
         produce((state: AdminStoreState) => {
            if (optionPairId) {
               delete state.graphCache[optionPairId];
            } else {
               state.graphCache = {};
            }
         })
      );
   },

   handleNodeAddition: async (newNode, optionPairId) => {
      try {
         const response = await fetchDb.post("/api/graph/node", {
            optionPairId,
            node: newNode,
         });

         const addedNode = response.data;
         set(
            produce((state: AdminStoreState) => {
               if (state.graphCache[optionPairId]) {
                  state.graphCache[optionPairId].nodes.push(
                     convertToRFNode(addedNode)
                  );
                  state.graphCache[optionPairId].timestamp = Date.now();
               }
            })
         );

         return addedNode;
      } catch (error) {
         console.error("Error adding node:", error);
         throw error;
      }
   },

   fetchLazyGraphData: async (optionPairId, setRfNodes, setRfEdges) => {
      if (!optionPairId) return;

      try {
         // First fetch only the root node and its immediate connections
         const response = await fetchDb.get<OptionPair>(
            `/api/option-pair/${optionPairId}/root-level`
         );

         // Process and display the initial nodes
         // ...

         // Then asynchronously load the rest of the graph
         fetchDb
            .get<OptionPair>(`/api/option-pair/${optionPairId}/full-graph`)
            .then((fullGraph) => {
               // Process and update with the full graph
               // ...
            })
            .catch((error) => {
               console.error("Error fetching full graph:", error);
            });
      } catch (error) {
         console.error("Error fetching initial graph data:", error);
      }
   },
});
